import { createSlice } from '@reduxjs/toolkit';
import { incrementMetric } from '../../../Services/Datadog/Datadog';

export const legalStatement = createSlice({
  name: 'LEGAL_STATEMENT',
  initialState: {}, // this comes from preloadedState in the store

  reducers: {
    setLegalStatement(state, action) {
      const hasReplacementStatement =
        typeof action.payload.statement === 'string' &&
        action.payload.statement.length > 0;
      return {
        ...state,
        ...action.payload,
        supplierLegalStatements: {
          ...state.supplierLegalStatements,
          ...action.payload.supplierLegalStatements,
        },
        // A flag-driven refetch has resolved once a real statement arrives.
        isAwaitingFlagDrivenRefetch: hasReplacementStatement
          ? false
          : state.isAwaitingFlagDrivenRefetch,
      };
    },
    updateIsLegalStatementMounted(state, action) {
      return {
        ...state,
        isLegalStatementMounted: action.payload?.isLegalStatementMounted,
      };
    },
    updateIsSuppliersListModalOpen(state, action) {
      return {
        ...state,
        isSuppliersListModalOpen: action.payload?.isSuppliersListModalOpen,
      };
    },
    /**
     * When dynamic height is enabled, the legal consent statement needs to be visible prior to navigating to the penultimate page.
     * Invoking setHandleSideEffectsOfLegalStatement from within the useNavigation hook followed by
     * updateShouldRenderLegalStatement from within <DynamicWidgetHeight/> takes care of that requirement.
     */
    setHandleSideEffectsOfLegalStatement(state, action) {
      return {
        ...state,
        handleSideEffectsOfLegalStatement:
          action.payload?.handleSideEffectsOfLegalStatement,
      };
    },
    setSideEffectDetailsForLegalStatement(state, action) {
      return {
        ...state,
        sideEffectDetailsForLegalStatement: {
          destinationPageNumber:
            action.payload?.sideEffectDetailsForLegalStatement
              .destinationPageNumber,
          navigationDirection:
            action.payload?.sideEffectDetailsForLegalStatement
              .navigationDirection,
        },
      };
    },
    updateShouldRenderLegalStatement(state, action) {
      return {
        ...state,
        shouldRenderLegalStatement: action.payload?.shouldRenderLegalStatement,
      };
    },
    updateIsLegalStatementRevealed(state, action) {
      return {
        ...state,
        isLegalStatementRevealed: action.payload?.isLegalStatementRevealed,
      };
    },
    updateHasStatementGraduallyDisappeared(state, action) {
      return {
        ...state,
        hasStatementGraduallyDisappeared:
          action.payload?.hasStatementGraduallyDisappeared,
      };
    },

    /**
     * Flags that a country-flag change has invalidated the legal statement and a
     * replacement is on its way. Dynamic height uses this to hold the widget
     * height (instead of shrinking) until the refetch resolves.
     */
    setAwaitingFlagDrivenRefetch(state, action) {
      return {
        ...state,
        isAwaitingFlagDrivenRefetch:
          action.payload?.isAwaitingFlagDrivenRefetch,
      };
    },

    getRequestFailed(state, action) {
      /**
       * If we have failed to get a LEGAL_STATEMENT we want to inform Datadog
       */
      incrementMetric({
        name: 'getLegalStatementFailed',
        tags: {
          error_thrown: 'Failed to get a Legal Statement from Eagle', // DO not change: this is used by Datadog
          locale_code: action.payload?.localeCode,
          subcategory_id: action.payload?.subcategoryId,
          include_supplier_legal_statements: 'true',
          error: action.payload?.error,
        },
      });

      /**
       * And ensure that the legalStatement is removed from state so that we do not submit a lead from a user who has not legally consented
       */
      state.statement = null;
      // The refetch is over (unsuccessfully), so stop holding the widget height.
      state.isAwaitingFlagDrivenRefetch = false;
    },

    setStatementOverride(state, action) {
      return {
        ...state,
        statementOverride: action.payload?.statementOverride,
      };
    },
  },
});

const { actions, reducer } = legalStatement;

export const {
  setLegalStatement,
  getRequestFailed,
  updateIsLegalStatementMounted,
  updateIsSuppliersListModalOpen,
  setHandleSideEffectsOfLegalStatement,
  updateShouldRenderLegalStatement,
  updateIsLegalStatementRevealed,
  updateHasStatementGraduallyDisappeared,
  setSideEffectDetailsForLegalStatement,
  setStatementOverride,
  setAwaitingFlagDrivenRefetch,
} = actions;

export default reducer;
